<template>
	<div class="multi-currency" :disabled="disabled ? true : null" @focus="onElementFocused">
		<div v-if="settings.targetValuePropertyName == 'debit' || settings.targetValuePropertyName == 'credit'">
		</div>
		<div v-for="(item, itemIndex) of modelValue" :key="item.id" class="mc-row">
			<nk-input class="currency-input" :disabled="disabled ? true : null" v-if="settings.targetValuePropertyName == 'debit' || settings.targetValuePropertyName == 'credit' && itemIndex == 0" :options="getDebitCreditInputOptions()" ref="dc" :tabindex="disabled ? 0 : itemIndex" v-model="item[settings.targetValuePropertyName]" @update:modelValue="val => onDebitCreditValueChanged(val, item)"></nk-input>
			<nk-dropdown :disabled="disabled ? true : null" v-if="settings.targetValuePropertyName == 'currency'" v-model="item.currency" :data="systemCurrencies" :options="componentOptions.currency" ref="currency" @update:modelValue="val => onCurrencyChange(val, item)" :tabindex="disabled ? 0 : itemIndex"></nk-dropdown>
			<nk-input class="currency-input" :disabled="disabled ? true : null" v-if="settings.targetValuePropertyName == 'currencyRate'" @focus="onCurrencyRateFocus(item)" :options="getDebitCreditInputOptions()" ref="currencyRate" :tabindex="disabled ? 0 : itemIndex" v-model="item[settings.targetValuePropertyName]" @update:modelValue="val => onCurrencyRateChanged(val, item)"></nk-input>
			<nk-input :disabled="disabled ? true : null" v-if="settings.targetValuePropertyName == 'debitCurrency' || settings.targetValuePropertyName == 'creditCurrency'" :options="getInputOptions(item)" ref="dcCurrency" :tabindex="disabled ? 0 : itemIndex" v-model="item[settings.targetValuePropertyName]" @update:modelValue="val => onDCCurrencyValueChanged(val, item)"></nk-input>
		</div>
	</div>
</template>

<script>
	import _ from 'lodash'
	import { mapGetters, mapState } from 'vuex'
	import { toFloat, rnd, truncate } from '~/plugins/force-to-number'

	export default {
		props: {
			modelValue: { type: Array },
			options: { type: Object },
			disabled: { type: Boolean, default: false },
		},
		emits: ['update:modelValue'],
		data() {
			return {
				settings: {
					isNameVisible: true,
					isSignVisible: true,
					showOnlyDefaultCurrency: false
				},
				currencyOptions: {
					mode: 'price',
					ltr: true,
					decimals: 0,
					...(this.options.inputClasses ? { inputClasses: this.options.inputClasses } : { inputClasses: { 'text-left': true } })
				},
				activeRowIndex: 0,
				componentOptions: {
					currency: {
						rowIdentifier: 'id',
						searchable: true,
						required: true,
						remoteSearch: false,
						display: function (item) { return item.name },
						searchableFields: ['name']
					}
				}
			}
		},
		computed: {
			...mapGetters({
				getAllCurrencies: 'currencies/companyCurrencies',
				getTodayRates: 'currencies/todayRates'
			}),
			...mapState('appContext', {
				status: (state) => state,
			}),
			systemCurrencies() {
				return this.getAllCurrencies.filter(x => !x.isDefault && x.isEnabled)
			},
			rows() {
				return this.getAllCurrencies.map(currency => {
					return _.extend({}, currency, { value: this.rowValue(currency.id) })
				})
			},
			defaultCurrency() {
				return this.getAllCurrencies.find(x => x.isDefault && x.isEnabled)
			}
		},
		watch: {
			options: {
				immediate: true,
				deep: true,
				handler(val) {
					this.settings = _.merge({}, this.settings, val)
				}
			}
		},
		methods: {
			focusRow(index) {
				for (var i in this.$refs) {
					if (i !== 'currency')
						this.$refs[i][index].$el.focus()
					else {
						this.$refs[i][index].$el.querySelector('input').focus()
					}
					break
				}
				return
			},
			rowValue(currencyId) {
				let item = _.head(this.modelValue.filter(x => x.id === currencyId))
				return (item || {}).value || 0
			},
			checkActiveRowIndex() {
				let index = this.rows.findIndex((r, index) => this.$refs[`mcrow-${index}`] === document.activeElement)

				if (index > -1) {
					this.activeRowIndex = index
				}
			},
			getInputOptions(item) {
				let opt = null
				if (item.currency && item.currency.decimals > 0) {
					opt = { decimals: item.currency.decimals }
				}
				return _.extend(this.currencyOptions, opt)
			},
			getDebitCreditInputOptions() {
				let opt = null
				if (this.defaultCurrency && this.defaultCurrency.decimals > 0) {
					opt = { decimals: this.defaultCurrency.decimals }
				}
				if (this.settings.isSignVisible) {
					opt = { icon2: this.status.company.currency.name }
				}
				return _.extend(this.currencyOptions, opt)
			},
			onCurrencyRateFocus(item) {
				if (item.calcDebitSource === '') {
					if (item.debit != 0)
						item.calcDebitSource = 'debit'
					else
						item.calcDebitSource = 'debitCurrency'
				}

				if (item.calcCreditSource === '') {
					if (item.credit != 0)
						item.calcCreditSource = 'credit'
					else
						item.calcCreditSource = 'creditCurrency'
				}
			},
			onDebitCreditValueChanged(value, item) {
				for (var row of this.modelValue) {
					row[this.settings.targetValuePropertyName] = value
				}

				if (item.currencyRate > 0)
					item[this.settings.targetValuePropertyName + 'Currency'] = truncate(value / item.currencyRate, item.currency ? item.currency.decimals : 0)

				item.calcCreditSource = 'credit'
				item.calcDebitSource = 'debit'

				this.$emit('update:modelValue', this.modelValue)
			},
			onDCCurrencyValueChanged(value, item) {
				let val = item.currencyRate ? value * item.currencyRate : 0
				if (this.defaultCurrency) {
					val = truncate(val, this.defaultCurrency.decimals)
				}
				item[this.settings.targetValuePropertyName.replace('Currency', '')] = val

				item.calcCreditSource = 'creditCurrency'
				item.calcDebitSource = 'debitCurrency'

				this.$emit('update:modelValue', this.modelValue)
			},
			onCurrencyRateChanged(val, item) {
				item.currencyRate = val
				let defaultFix = this.defaultCurrency ? this.defaultCurrency.decimals : 0
				let itemFix = item.currency ? item.currency.decimals : 0

				if (item.calcCreditSource == 'creditCurrency')
					item.credit = truncate(item.creditCurrency * val, defaultFix)
				else if (val != 0)
					item.creditCurrency = truncate(item.credit / val, itemFix)

				if (item.calcDebitSource == 'debitCurrency')
					item.debit = truncate(item.debitCurrency * val, defaultFix)
				else if (val != 0) {
					item['debitCurrency'] = truncate(item.debit / val, itemFix)
				}
				this.$emit('update:modelValue', this.modelValue)
			},
			onCurrencyChange(val, item) {
				if (!this.getTodayRates || this.getTodayRates.length == 0 || !val || !this.defaultCurrency)
					return

				let rates = this.getTodayRates.sort((a, b) => (b.date > a.date) ? 1 : ((a.date > b.date) ? -1 : 0))

				let rate = rates.find(x => x.baseCurrency.id == this.defaultCurrency.id && x.currency.id == val.id)

				if (rate) {
					item.currencyRate = rate.rate
					this.onCurrencyRateChanged(rate.rate, item)
				}
			},
			onElementFocused() {
				this.focusRow(0)
			},
			subscribeKeys() {
				this.$keyHandler.Subscribe({
					name: 'multi-currency-forward',
					keyCode: [40, 13, 9],
					ctrlKey: false,
					altKey: false,
					when: 'before',
					el: this.$el,
					context: this,
					handler: function (sender, e) {
						e.preventDefault()

						this.checkActiveRowIndex()

						var handled = false

						if (this.activeRowIndex < this.modelValue.length - 1) {
							let index = this.activeRowIndex + 1
							this.focusRow(index)
							handled = true
						}

						return handled
					}
				}) // forward

				this.$keyHandler.Subscribe({
					name: 'multi-currency-backward',
					keyCode: [38],
					when: 'before',
					el: this.$el,
					context: this,
					handler: function (sender, e) {
						e.preventDefault()

						this.checkActiveRowIndex()

						var handled = false

						if (this.activeRowIndex > 0) {
							let index = this.activeRowIndex - 1
							this.focusRow(index)
							handled = true
						}

						return handled
					}
				}) // backward

				this.$keyHandler.Subscribe({
					name: 'multi-currency-backward-shift',
					keyCode: [9],
					shiftKey: true,
					when: 'before',
					el: this.$el,
					context: this,
					handler: function (sender, e) {
						e.preventDefault()

						this.checkActiveRowIndex()

						var handled = false

						if (this.activeRowIndex > 0) {
							let index = this.activeRowIndex - 1
							this.focusRow(index)
							handled = true
						}

						return handled
					}
				}) // backward with shift
			}
		}
	}
</script>

<style lang="less"
	src="~/styles/multi-currency.less"></style>